import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronDown, LayoutGrid, Truck, Cloud, Siren, Building2, Briefcase, Bot, Cpu, Sparkles, Handshake } from "lucide-react";

/**
 * Microsoft-style 4-tile Platform icon.
 * Rounded squares in 4 brand accents — used on the Platform mega-menu trigger.
 */
function PlatformTilesIcon({ className = "w-4 h-4" }) {
  return (
    <svg viewBox="0 0 24 24" className={className} aria-hidden="true">
      <rect x="2"  y="2"  width="9" height="9" rx="1.8" fill="#F25022" />
      <rect x="13" y="2"  width="9" height="9" rx="1.8" fill="#7FBA00" />
      <rect x="2"  y="13" width="9" height="9" rx="1.8" fill="#00A4EF" />
      <rect x="13" y="13" width="9" height="9" rx="1.8" fill="#FFB900" />
    </svg>
  );
}

/**
 * PlatformMegaMenu — Master Platform Taxonomy · single source of truth for the public header.
 * 7 top-level categories × modules · additive-only structure.
 * Live modules link normally; future modules render muted with a "Soon" chip.
 * Never rename or remove a category — new verticals must slot inside an existing one.
 */

export const MASTER_STRUCTURE = [
  {
    code: "marketplace", label: "Marketplace", icon: Truck, tone: "text-emerald-600",
    modules: [
      { label: "Rent Equipment",            to: "/marketplace" },
      { label: "New Equipment Buy/Sell",    to: "/buy-new" },
      { label: "Used Equipment Buy/Sell",   to: "/used" },
      { label: "Industrial Scrap Disposal", to: "/scrap" },
      { label: "Industrial Materials Hub",  to: "/industrial-materials" },
      { label: "Workforce",                 to: "/workforce" },
      { label: "Inspection",                to: "/inspection" },
      { label: "Suppliers Directory",       to: "/suppliers-directory" },
    ],
  },
  {
    code: "equipment_cloud", label: "Equipment Cloud", icon: Cloud, tone: "text-blue-600",
    modules: [
      { label: "Fleet Cloud",        to: "/product/fleet-cloud" },
      { label: "Maintenance Cloud",  to: "/product/maintenance-cloud" },
      { label: "Vendor Cloud",       soon: true },
      { label: "Inspection Cloud",   soon: true },
      { label: "Procurement Cloud",  soon: true },
    ],
  },
  {
    code: "partner_network", label: "Partner Network", icon: Handshake, tone: "text-emerald-600",
    modules: [
      { label: "Become a Partner",     to: "/become-partner" },
      { label: "Partner Login",        to: "/partner-login" },
      { label: "Partner Program Info", to: "/become-partner" },
    ],
  },
  {
    code: "service_network", label: "Service Network", icon: Siren, tone: "text-rose-600",
    modules: [
      { label: "AMC Marketplace",           to: "/amc", live: true },
      { label: "FMC Marketplace",           to: "/fmc", live: true },
      { label: "Breakdown 24×7",            to: "/breakdown", live: true },
      { label: "Equipment Intelligence",    to: "/intel" },
    ],
  },
  {
    code: "business_services", label: "Business Services", icon: Briefcase, tone: "text-amber-600",
    modules: [
      { label: "Logistics Network",    to: "/logistics", live: true },
      { label: "OEM Network",          to: "/brands", live: true },
      { label: "Finance",              to: "/finance", live: true },
      { label: "Insurance Marketplace",to: "/insurance", live: true },
      { label: "Insurance Renewal",    to: "/insurance/renewal", live: true },
      { label: "Pay",                  soon: true },
      { label: "Academy",              soon: true },
      { label: "Compliance Services",  soon: true },
      { label: "Consulting Services",  to: "/experts", live: true },
    ],
  },
  {
    code: "enterprise", label: "Enterprise Solutions", icon: Building2, tone: "text-indigo-600",
    modules: [
      { label: "Vendor Management",   soon: true },
      { label: "RFQ & Tender Hub",    soon: true },
      { label: "Contractor Network",  soon: true },
      { label: "Procurement Hub",     soon: true },
    ],
  },
  {
    code: "ai_intelligence", label: "AI & Intelligence", icon: Bot, tone: "text-fuchsia-600",
    publicHidden: true,
    modules: [
      { label: "AI Workforce",                 to: "/ai-workforce" },
      { label: "AI CEO Advisor",               adminOnly: true },
      { label: "AI CRM Manager",               adminOnly: true },
      { label: "AI Sales Coach",               adminOnly: true },
      { label: "AI Business Analyst",          adminOnly: true },
      { label: "AI Customer Success Manager",  adminOnly: true },
      { label: "AI Marketing Manager",         soon: true },
      { label: "AI Operations Advisor",        soon: true },
      { label: "AI Workforce Manager",         soon: true },
      { label: "AI CMO",                       soon: true },
      { label: "AI Content Studio",            soon: true },
      { label: "AI Reel Studio",               soon: true },
      { label: "AI Reputation Manager",        soon: true },
      { label: "AI Growth Strategist",         soon: true },
    ],
  },
  {
    code: "ehi_os", label: "EquipmentHub OS", icon: Cpu, tone: "text-slate-700",
    publicHidden: true,
    modules: [
      { label: "Unified Platform Layer", note: "Every module above runs on this shared OS · auth · billing · audit · governance · notifications · signed storage." },
    ],
  },
];

// ---------- Desktop trigger + panel ----------
export function PlatformMegaMenu() {
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState("marketplace");
  const wrapRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => wrapRef.current && !wrapRef.current.contains(e.target) && setOpen(false);
    const onEsc = (e) => e.key === "Escape" && setOpen(false);
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onEsc);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onEsc);
    };
  }, [open]);

  const activeCat = MASTER_STRUCTURE.find((c) => c.code === active) || MASTER_STRUCTURE[0];

  // Categories with zero live modules are hidden from the public rail
  // (still available in code so a single flip to `live:true` re-lights them).
  const hasLive = (cat) => (cat.modules || []).some((m) => m.to || m.live);
  const VISIBLE_CATEGORIES = MASTER_STRUCTURE.filter((c) => c.publicHidden || hasLive(c));

  return (
    <div className="relative" ref={wrapRef} data-testid="platform-mega-menu">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className={`px-3 xl:px-4 py-2 text-sm xl:text-base font-semibold rounded-md transition-colors inline-flex items-center gap-1.5 ${
          open ? "text-ehi-primary bg-slate-100" : "text-slate-700 hover:text-ehi-primary hover:bg-slate-50"
        }`}
        data-testid="platform-mega-trigger"
      >
        <PlatformTilesIcon className="w-4 h-4 xl:w-5 xl:h-5" /> Platform
        <ChevronDown className={`w-4 h-4 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div
          className="absolute left-0 mt-2 w-[min(1080px,94vw)] bg-white rounded-xl border border-slate-200 shadow-2xl overflow-hidden z-50"
          data-testid="platform-mega-panel"
        >
          <div className="grid grid-cols-[280px_1fr]">
            {/* Category rail */}
            <aside className="bg-gradient-to-b from-[#0B1F3A] via-[#0F2A4F] to-[#0B1F3A] text-white p-3">
              <div className="text-[10px] tracking-[0.22em] uppercase font-black text-ehi-accent px-2 pb-2" style={{ fontFamily: "'IBM Plex Mono', ui-monospace, monospace" }}>
                Master Taxonomy · v2026.02
              </div>
              <div className="space-y-0.5">
                {VISIBLE_CATEGORIES.map((cat, i) => {
                  const Icon = cat.icon;
                  const isActive = active === cat.code;
                  return (
                    <button
                      key={cat.code}
                      onMouseEnter={() => setActive(cat.code)}
                      onFocus={() => setActive(cat.code)}
                      onClick={() => setActive(cat.code)}
                      className={`w-full flex items-center gap-2.5 px-3 py-2.5 rounded-sm text-sm font-medium text-left transition-colors ${
                        isActive
                          ? "bg-white/12 text-white shadow-[inset_3px_0_0_0_#F97316]"
                          : "text-slate-300 hover:bg-white/8 hover:text-white"
                      }`}
                      data-testid={`platform-cat-${cat.code}`}
                    >
                      <span className={`w-6 h-6 rounded-sm bg-white/10 grid place-items-center ${cat.tone}`}>
                        <Icon className="w-3.5 h-3.5" />
                      </span>
                      <span className="flex-1 truncate">{cat.label}</span>
                      <span className="text-[10px] font-mono-tab text-white/40">{String(i + 1).padStart(2, "0")}</span>
                    </button>
                  );
                })}
              </div>
            </aside>

            {/* Module grid */}
            <div className="p-5">
              <div className="flex items-center gap-2 mb-3 pb-3 border-b border-slate-100">
                <activeCat.icon className={`w-5 h-5 ${activeCat.tone}`} />
                <h3 className="font-display font-black text-lg text-slate-900">{activeCat.label}</h3>
                <span className="ml-auto text-[10px] font-mono-tab text-slate-400 uppercase tracking-widest">
                  {activeCat.modules.length} module{activeCat.modules.length !== 1 ? "s" : ""}
                </span>
              </div>

              {activeCat.publicHidden ? (
                <div className="rounded-lg bg-gradient-to-br from-slate-900 via-[#0F2A4F] to-slate-900 text-white p-8 text-center">
                  <div className="w-14 h-14 rounded-full bg-white/10 grid place-items-center mx-auto mb-3">
                    <activeCat.icon className={`w-6 h-6 ${activeCat.tone}`} />
                  </div>
                  <div className="text-[10px] tracking-[0.22em] uppercase font-black text-ehi-accent mb-2" style={{ fontFamily: "'IBM Plex Mono', ui-monospace, monospace" }}>
                    Enterprise Layer · Private Access
                  </div>
                  <div className="font-display font-black text-2xl mb-2">{activeCat.label}</div>
                  <p className="text-sm text-white/70 max-w-md mx-auto">
                    {activeCat.code === "ai_intelligence"
                      ? "AI workforce and intelligence modules are reserved for the EquipmentHub internal team and enterprise customers. Roadmap is intentionally kept private."
                      : "The unified platform layer powering EquipmentHub India. Available to enterprise customers on request."}
                  </p>
                  <a href="mailto:info@equipmenthubindia.com?subject=Enterprise access enquiry" className="mt-4 inline-flex items-center gap-1.5 px-3 py-2 rounded-md bg-ehi-accent text-white text-xs font-black uppercase tracking-wider hover:opacity-90">
                    Request enterprise access
                  </a>
                </div>
              ) : activeCat.code === "ehi_os" ? (
                <div className="rounded-lg bg-gradient-to-br from-slate-900 via-[#0F2A4F] to-slate-900 text-white p-5">
                  <div className="flex items-center gap-2 text-[10px] tracking-[0.22em] uppercase font-black text-ehi-accent mb-2" style={{ fontFamily: "'IBM Plex Mono', ui-monospace, monospace" }}>
                    <Sparkles className="w-3 h-3" /> Unified Platform Layer
                  </div>
                  <div className="font-display font-black text-2xl mb-2">One OS · Six product families</div>
                  <p className="text-sm text-white/70 max-w-xl">
                    Auth, billing, audit, governance, notifications, signed storage, AI Workforce, retention automation and telemetry are shared across every module above. Every new vertical inherits the OS on day one — no bolt-ons, no data silos.
                  </p>
                  <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
                    {["Auth · RBAC · Support Gates","Signed File Storage","Audit + Super-Admin Log","Rate Limiting","Retention Automation","Push Notifications","AI Workforce (Rec-only)","Foundation Modules (7)"].map((f) => (
                      <div key={f} className="bg-white/8 rounded px-2 py-1.5 border border-white/12 font-medium">{f}</div>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-1.5">
                  {activeCat.modules.map((m) => <ModuleRow key={m.label} m={m} />)}
                </div>
              )}
            </div>
          </div>

          <div className="border-t border-slate-100 bg-slate-50 px-5 py-2.5 text-[11px] text-slate-500 flex items-center justify-between">
            <span>Additive-only structure · new verticals slot inside an existing category</span>
            <span className="font-mono-tab uppercase tracking-widest text-slate-400">EquipmentHub India V3.1</span>
          </div>
        </div>
      )}
    </div>
  );
}

function ModuleRow({ m }) {
  const base = "flex items-center justify-between gap-2 px-3 py-2 rounded-md text-sm transition-colors border";
  if (m.to) {
    return (
      <Link to={m.to} className={`${base} border-transparent text-slate-700 hover:text-ehi-primary hover:bg-white hover:border-slate-200`} data-testid={`platform-mod-${m.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
        <span className="font-medium truncate">{m.label}</span>
        <span className="text-[9px] font-black uppercase tracking-widest text-emerald-600">Live</span>
      </Link>
    );
  }
  if (m.adminOnly) {
    return (
      <div className={`${base} border-transparent text-slate-600 opacity-90`} data-testid={`platform-mod-${m.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
        <span className="font-medium truncate">{m.label}</span>
        <span className="text-[9px] font-black uppercase tracking-widest text-blue-600">Admin</span>
      </div>
    );
  }
  return (
    <div className={`${base} border-dashed border-slate-200 text-slate-400`} data-testid={`platform-mod-${m.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
      <span className="font-medium truncate">{m.label}</span>
      <span className="text-[9px] font-black uppercase tracking-widest text-slate-400">Soon</span>
    </div>
  );
}

// ---------- Mobile accordion ----------
export function PlatformMegaMenuMobile({ onNavigate }) {
  const [openCat, setOpenCat] = useState("marketplace");
  return (
    <div className="space-y-1" data-testid="platform-mega-mobile">
      {MASTER_STRUCTURE.filter((c) => c.publicHidden || (c.modules || []).some((m) => m.to || m.live)).map((cat) => {
        const Icon = cat.icon;
        const isOpen = openCat === cat.code;
        if (cat.publicHidden) {
          // Public-hidden categories show only the header, not their internal modules.
          return (
            <div key={cat.code} className="border border-slate-200 rounded-md overflow-hidden opacity-70">
              <div className="w-full flex items-center gap-2 px-3 py-2.5 bg-slate-50 text-left">
                <Icon className={`w-4 h-4 ${cat.tone}`} />
                <span className="font-bold text-sm text-slate-800 flex-1">{cat.label}</span>
                <span className="text-[9px] font-black uppercase tracking-widest text-slate-400">Enterprise</span>
              </div>
            </div>
          );
        }
        return (
          <div key={cat.code} className="border border-slate-200 rounded-md overflow-hidden">
            <button
              onClick={() => setOpenCat(isOpen ? null : cat.code)}
              className="w-full flex items-center gap-2 px-3 py-2.5 bg-slate-50 hover:bg-slate-100 text-left"
              data-testid={`platform-mob-cat-${cat.code}`}
            >
              <Icon className={`w-4 h-4 ${cat.tone}`} />
              <span className="font-bold text-sm text-slate-800 flex-1">{cat.label}</span>
              <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${isOpen ? "rotate-180" : ""}`} />
            </button>
            {isOpen && (
              <div className="p-2 space-y-0.5">
                {cat.modules.map((m) => (
                  m.to ? (
                    <Link
                      key={m.label}
                      to={m.to}
                      onClick={onNavigate}
                      className="flex items-center justify-between px-2 py-2 rounded text-sm text-slate-700 hover:bg-slate-50"
                    >
                      <span>{m.label}</span>
                      <span className="text-[9px] font-black uppercase text-emerald-600">Live</span>
                    </Link>
                  ) : (
                    <div key={m.label} className="flex items-center justify-between px-2 py-2 rounded text-sm text-slate-400">
                      <span>{m.label}</span>
                      <span className="text-[9px] font-black uppercase">{m.adminOnly ? "Admin" : "Soon"}</span>
                    </div>
                  )
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

export default PlatformMegaMenu;
